<template>
  <div class="container">
    <transition name="fade">
      <div v-if="Object.keys(validatedCartData).length" class="inner-container"
           :class="{'inner-container--tourist': isTouristFlow}">
        <checkout-cart-products :products="validatedCartData.entries"
                                :is-free-e-sim-srb="isFreeESimSrb"
                                :hide-recurring-price="isTouristFlow"
                                :class="{'for-tourist': isTouristFlow}"
                                @delete-item="handleDelete"/>
        <checkout-cart-totals
            :recurring-price="monthlyTotalPrice"
            :is-free-e-sim-srb="isFreeESimSrb"
            :total-price="totalPrice"
            :buyout-price="buyoutData"
            :class="{'for-tourist': isTouristFlow}"/>
        <checkout-email-form
            v-if="isTouristFlow"
            :is-free-e-sim-srb="isFreeESimSrb"
            :lang="pageLanguage"/>
        <checkout-cart-watches v-if="validatedCartData.canAddSmartWatches && additionalOfferingsProducts.length"
                               :products="additionalOfferingsProducts"
                               :segment="$props.segment"
                               @refresh-cart="refreshCart"
                               @open-specification="handleOpenSpecification"/>
        <checkout-cart-equipment v-if="equipmentList.length"
                                 :equipment-list="equipmentList"
                                 :segment="$props.segment"
                                 @refresh-cart="refreshCart"/>
        <checkout-cart-addons v-if="addonList.length"
                              :addon-list="addonList"
                              @refresh-cart="refreshCart"/>
      </div>
    </transition>
    <product-specs-modal :modal-visible="productModalVisible"
                         :device-info="deviceData"
                         @close="productModalVisible = false"/>
    <product-replacement-dialog :visible="productReplacementDialogVisible"
                                :product-info="productReplacement"
                                @refresh-cart="refreshCartAfterSubsidyChange"
                                @cancel="cancelSubsidyReplace"/>
    <buyout-dialog :visible="buyoutDialogVisible"
                   :buyout-data="buyoutData"
                   @continue="continueWithBuyoutCO"
                   @cancel="cancelBuyout"/>
    <info-modal :visible="showInfoModal"
                :info-icon="infoModal[infoModalType].icon"
                :info-title="infoModal[infoModalType].title"
                :dynamic-description="infoModal[infoModalType].description"
                :cta-text="infoModal[infoModalType].cta"
                :close-visible="infoModal[infoModalType].closeVisible"
                :redirect="infoModal[infoModalType].redirect"
                prevent-backdrop-close
                @click="goToIdentify"
                @close="closeBusiness"/>
  </div>
</template>

<script>
import { validateCartAndGetData, fetchAddons, fetchAccessories } from '@/api/checkout'
import { deleteFromCart } from '../../../api/cart'
import { fetchPlp } from '@/api/plp'
import { getCookie } from '@/utils/cookieController'
import cartComponentsProps from '../config/cartComponentsProps'
import CheckoutCartProducts from './checkout-cart-products'
import CheckoutCartTotals from './checkout-cart-totals'
import CheckoutCartWatches from './checkout-cart-watches'
import ProductSpecsModal from './product-specs-modal'
import { fetchProduct } from '../../../api/pdp'
import CheckoutCartAddons from './checkout-cart-addons'
import CheckoutCartEquipment from './checkout-cart-equipment'
import ProductReplacementDialog from './product-replacement-dialog'
import BuyoutDialog from './buyout-dialog'
import InfoModal from '../../common/modal/info-modal'
import { fail } from '../config/orderConfirmationProps'
import { addDataLayerEvent } from '../../../services/dataLayer'
import checkoutDataLayer from '../mixins/checkoutDataLayer'
import { checkoutInfoModal } from '../config/infoModalContent'
import CheckoutEmailForm from './checkout-email-form.vue'
import { getTouristFlowDataFromUrl } from '@/utils/urlMethods'
import { customEvent } from '@/services/customEventBus'
import { lsSave } from '../../../utils/localStorageController'
import { localStorageKeys } from '@/config/constants'

export default {
  name: 'the-checkout-preview-cart',
  mixins: [checkoutDataLayer],
  components: {
    CheckoutEmailForm,
    BuyoutDialog,
    ProductReplacementDialog,
    CheckoutCartEquipment,
    CheckoutCartAddons,
    ProductSpecsModal,
    CheckoutCartWatches,
    CheckoutCartTotals,
    CheckoutCartProducts,
    InfoModal
  },
  data () {
    return {
      validatedCartData: {},
      additionalOfferingsProducts: [],
      productModalVisible: false,
      deviceData: {},
      addonList: [],
      equipmentList: [],
      cartId: null,
      productReplacement: {},
      productReplacementDialogVisible: false,
      buyoutData: null,
      buyoutDialogVisible: false,
      showErrScreen: false,
      showButtons: false,
      showText: false,
      showInfoModal: false,
      infoModalType: 'maxLimit'
    }
  },
  props: { ...cartComponentsProps },
  computed: {
    monthlyTotalPrice () {
      return this.isTouristFlow ? null : this.validatedCartData?.monthlyTotalPrice?.formattedValue || null
    },
    totalPrice () {
      return this.validatedCartData?.oneTimeTotalPrice?.formattedValue || null
    },
    isFreeESimSrb () {
      return this.validatedCartData?.entries?.some(
        entry => entry.product?.code === 'MTS-besplatan-esim'
      )
    },
    fail () {
      return { ...fail(this) }
    },
    infoModal () {
      return { ...checkoutInfoModal(this) }
    },
    isTouristFlow () {
      return !!getTouristFlowDataFromUrl()
    },
    pageLanguage () {
      return this.$i18n.locale || ''
    }
  },
  methods: {
    fetchCartData () {
      this.cartId = getCookie('guid')
      this.$emit('child-loading', true)
      if (this.cartId) {
        this.$loader.show()
        validateCartAndGetData(this.cartId, this.pageLanguage).then(({ data }) => {
          if (data.errors && data.errors.errors[0].reason === 'blacklist') {
            this.$emit('blacklist')
          }
          if (data.errors && data.errors.errors[0].reason === 'max_limit') {
            this.showInfoModal = true
            this.infoModalType = 'maxLimit'
            this.$emit('show-nav', false)
          }
          if (data.errors && data.errors.errors[0].reason === 'business') {
            this.showInfoModal = true
            this.infoModalType = 'businessNumber'
          }

          if (data.cartModifications && data.cartModifications[0].statusCode === 'productsNeedReplace') { // da li se moze desiti da postoji vise uredjaja od kojih samo jedan treba da se zameni?
            this.showProductReplacementDialog(data.cartModifications[0].entry)
          } else if (data.cart.buyoutCO) {
            this.showBuyoutDialog(data.cart)
            this.validatedCartData = data.cart || {}
          } else {
            this.validatedCartData = data.cart || {}
            if (!this.isTouristFlow) {
              this.fetchAdditionalOfferings()
              this.getAddons(data.cart.tariffType)
              this.getAccessories()
            }
            this.$emit('child-loading', false)
            this.checkoutDataLayer(this.validatedCartData.entries, this.$props.step, this.$props.userType)
          }
        }).finally(() => {
          this.$loader.hide()
        })
      }
    },
    fetchAdditionalOfferings () {
      if (!this.validatedCartData.canAddSmartWatches) return
      fetchPlp({
        searchQueryContext: 'ADDITIONAL_OFFERINGS',
        pageSize: 10,
        segmentGroup: this.$props.segment
      })
        .then(({ data }) => {
          this.additionalOfferingsProducts = [...data.products] || []
        })
        .catch(e => {
          this.additionalOfferingsProducts = []
        })
    },
    getAddons (tariffType) {
      fetchAddons(tariffType, { pageSize: 10 })
        .then(({ data }) => {
          this.addonList = [...data.results]
        })
        .catch(e => {
          this.addonList = []
        })
    },
    getAccessories () {
      fetchAccessories(this.cartId, 10)
        .then(({ data }) => {
          this.equipmentList = [...data.products]
        })
        .catch(e => {
          this.equipmentList = []
        })
    },
    refreshCart (slideToTop = false) {
      this.fetchCartData()
      if (slideToTop) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        })
      }
    },
    refreshCartAfterSubsidyChange (cartData) {
      this.validatedCartData = cartData.cart || {}
      this.fetchAdditionalOfferings()
      this.getAddons(cartData.cart.tariffType)
      this.getAccessories()
      this.productReplacementDialogVisible = false
      this.$emit('child-loading', false)
    },
    handleDelete (entryNumber, deleted) {
      if (!entryNumber) return
      this.$loader.show()
      const dataToSend = {
        cartId: this.cartId,
        entryNumber: entryNumber
      }
      deleteFromCart(dataToSend)
        .then(() => {
          this.fetchCartData()
          this.deletedDataLayer(deleted)
        })
        .catch(e => {
          this.toast('error', {
            message: this.$t('errors.defaultError')
          })
        })
        .finally(() => {
          this.$loader.hide()
        })
    },
    handleOpenSpecification (e) {
      this.$loader.show()
      fetchProduct(e.code, this.$props.segment)
        .then(({ data }) => {
          this.deviceData = data
          this.productModalVisible = true
        })
        .catch(() => {
          this.deviceData = {}
          this.toast('error', {
            message: this.$t('errors.defaultError')
          })
        })
        .finally(() => {
          this.$loader.hide()
        })
    },
    showProductReplacementDialog (replacementEntry = {}) {
      this.productReplacement = { ...replacementEntry }
      this.productReplacementDialogVisible = true
      this.$emit('child-loading', true)
    },
    cancelSubsidyReplace () {
      this.productReplacementDialogVisible = false
      setTimeout(() => {
        this.$emit('child-loading', false)
        this.$emit('go-back')
      }, 50)
    },
    showBuyoutDialog (cartData) {
      this.buyoutData = cartData.buyoutCO
      this.buyoutDialogVisible = true
      this.$emit('child-loading', true)
    },
    cancelBuyout () {
      this.buyoutDialogVisible = false
      setTimeout(() => {
        this.$emit('go-back')
      }, 50)
    },
    continueWithBuyoutCO () {
      this.buyoutDialogVisible = false
      this.$emit('child-loading', false)
      this.fetchAdditionalOfferings()
      this.getAddons(this.validatedCartData.tariffType)
      this.getAccessories()
    },
    deletedDataLayer (deleted) {
      const hasBothPrices = !!deleted.monthlyPrice && !!deleted.oneTimePrice
      const combinePrice = hasBothPrices ? deleted?.monthlyPrice?.price?.value * deleted?.monthlyPrice.cycleEnd + deleted?.oneTimePrice?.price?.value : null
      let price = hasBothPrices ? combinePrice : deleted?.oneTimePrice?.price?.value || deleted?.monthlyPrice?.price?.value * deleted?.monthlyPrice.cycleEnd
      price = price.toString().replace('.', ',')
      addDataLayerEvent({
        event: 'eec.remove',
        ecommerce: {
          add: {
            actionField: {
              list: deleted.product.categories?.[0]?.name
            }
          },
          products: [{
            id: deleted.product.code,
            name: deleted.product.name,
            category: deleted.product.categories?.[0]?.name,
            subcategory: deleted.product.categories?.[0]?.name,
            variant: deleted.variant?.variantOptionQualifiers?.[0]?.value,
            brand: deleted.product.brands?.[0]?.name,
            dimension1: deleted?.monthlyPrice?.cycleEnd || 'one time',
            price: price,
            quantity: deleted.quantity
          }]
        }
      })
    },
    goToIdentify () {
      this.showInfoModal = false
      setTimeout(() => {
        this.$emit('go-to-identification')
      }, 0)
    },
    closeBusiness (redirect = false) {
      if (redirect) {
        this.$loader.show()
        window.location.replace('/')
      }
    }
  },
  watch: {
    validatedCartData: {
      handler (newValue) {
        if (newValue) {
          this.$nextTick(() => {
            customEvent.$emit('FREE_CART', newValue?.oneTimeTotalPrice?.value === 0 && newValue?.monthlyTotalPrice?.value === 0)
          })
        }
      },
      immediate: true,
      deep: true
    }
  },
  created () {
    this.fetchCartData()
    if (this.pageLanguage) lsSave(localStorageKeys.touristCO, location.search)
  }
}
</script>

<style scoped lang="scss">
.inner-container {
  max-width: 1000px;
  margin: auto;
  padding: 4rem 0 2rem;

  @media (map_get($breakpoints, sm)) {
    padding-bottom: 20rem;

    &--tourist {
      padding-bottom: 2rem;
    }
  }
}

.error-screen {
  margin: 12rem 0;
  @media (map_get($breakpoints, sm)) {
    margin: 9rem 0;
  }
  @media (map_get($breakpoints, md)) {
    margin: 7rem 0;
  }
  @media (map_get($breakpoints, xl)) {
    margin: 12rem 0;
  }
}
</style>
